import { get as _get } from 'lodash';
import { v4 as uuid } from 'uuid';

import { PhotoInfo } from '../types/type';
import {
  DatadogErrorTracker,
  ErrorCategories,
  ErrorSeverity,
  FeatureAreas,
  PhotoErrorTypes,
} from '../utils/DatadogErrorTracker';
import { getUnformattedClaimNumber } from '../utils/Utilities';

export async function getPhotoDocuments(claimNumber: string, urlKey: string, sessionId: string, custKey: string) {
  try {
    const headers = new Headers();
    headers.append('urlid', urlKey);
    headers.append('afe-session-id', sessionId);
    headers.append('Authorization', 'Bearer ' + custKey);
    headers.append('content-type', 'application/json');
    headers.append('afe-trace-id', uuid());

    const URI = `${process.env['NX_DCV2_API_BASE_URL']}documents/${getUnformattedClaimNumber(claimNumber)}`;
    const response = await fetch(URI, {
      method: 'GET',
      headers: headers,
    });
    const responseData = await response.json();

    if (_get(responseData, 'data') && Array.isArray(_get(responseData, 'data'))) {
      const photos = responseData.data.filter((p: PhotoInfo) => !!p);

      // Track successful photo documents fetch
      if (photos.length > 0) {
        DatadogErrorTracker.trackAction('photo-documents-fetched', {
          photoCount: photos.length,
          claimNumber: getUnformattedClaimNumber(claimNumber),
          featureArea: FeatureAreas.CLAIM_DETAIL_PHOTOS,
        });
      }

      return photos;
    } else {
      throw new Error(_get(response, 'status', '').toString());
    }
  } catch (error) {
    // Track photo documents fetch error
    DatadogErrorTracker.trackPhotoError(DatadogErrorTracker.normalizeError(error), {
      errorType: PhotoErrorTypes.DOCUMENTS_FETCH_FAILED,
      errorCategory: ErrorCategories.PHOTO_API,
      featureArea: FeatureAreas.CLAIM_DETAIL_PHOTOS,
      errorSeverity: ErrorSeverity.ERROR,
      claimNumber: getUnformattedClaimNumber(claimNumber),
      networkStatus: DatadogErrorTracker.getNetworkStatus(),
      recoverable: true,
      userAction: 'fetching-photo-documents',
    });

    return undefined;
  }
}

export async function retrievePhoto(url: string, mimeType: string, urlKey: string, sessionId: string, custKey: string) {
  try {
    return await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'image/*',
        Authorization: 'Bearer ' + custKey,
        urlid: urlKey,
        'afe-trace-id': uuid(),
        'afe-session-id': sessionId,
        mimeType: mimeType,
        responseType: 'blob',
      },
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const b = await response.blob();

      // Validate blob
      if (!b || b.size === 0) {
        throw new Error('Empty blob received from server');
      }

      const objectURL = URL.createObjectURL(b);
      return objectURL;
    });
  } catch (error) {
    // Track photo retrieval error with detailed context
    DatadogErrorTracker.trackPhotoError(DatadogErrorTracker.normalizeError(error), {
      errorType: PhotoErrorTypes.RETRIEVAL_FAILED,
      errorCategory: ErrorCategories.PHOTO_LOAD,
      featureArea: FeatureAreas.CLAIM_DETAIL_PHOTOS,
      errorSeverity: ErrorSeverity.ERROR,
      photoUrl: url,
      mimeType: mimeType,
      networkStatus: DatadogErrorTracker.getNetworkStatus(),
      recoverable: true,
      userAction: 'loading-photo-image',
    });

    return '';
  }
}
